import React, { useState, useEffect } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import ReactPaginate from 'react-paginate';
import { FaSearch } from 'react-icons/fa'; // Import icons
import { allJobsInfo, manualCapture } from '../../../apiManager/admin/JobsManagement.js';
import { Loader } from '../../../components/base/Loader';
import ConfirmationModal from '../../../components/app/shared/CustomModal.jsx';
import api from '../../../apiManager/api.js';
import Papa from 'papaparse'
import { cancelJob, cancelJobByAdmin } from '../../../apiManager/houseOwner/matchShvoller.js';
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Search } from "lucide-react";

function CustomerTable() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateSortBy, setDateSortBy] = useState('Newest First');
    const [showCancelModal, setShowCancelModal] = useState(false); //
    const [jobId, setJobId] = useState(''); //
    const [shovelerId, setShovelerId] = useState(''); //
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(10);
    const rowsPerPage = 8;

    useEffect(() => {
        (async function () {
            const res = await api.get(`/job/getAllJobsCount`);
            setTotalCount(res.data.count);
        })();
    }, [])

    useEffect(() => {
        const getJobsData = async () => {
            try {
                setLoading(true);
                const res = await allJobsInfo(currentPage + 1);
                console.log(res)
                if (!res) setJobs([]);
                else setJobs(res);
            } catch (error) {
                setError(error.message || "An error occurred while fetching data");
            } finally {
                setLoading(false);
            }
        }
        getJobsData();
    }, [jobId, currentPage]);

    const handleCapture = async () => {
        try {
            setLoading(true);

            const res = await manualCapture(jobId, shovelerId, 'admin');
            if (res && res.error) {
                setError(res.error);
                setLoading(false);
                setJobId('');
                setShovelerId('');
                return
            }

            alert('Payment Captured Successfully');
            setShowCancelModal(false); // Close the modal before performing the action
            setLoading(false);
        } catch (error) {
            setError(error.message || "An error occurred while capturing payment");
        }
        finally {
            setLoading(false);
            setJobs(jobs.filter((referral) => referral._id !== jobId));
        }
        setJobId('');
        setShovelerId('');
    }

    const handleManualCaptureClick = (row) => {
        setShowCancelModal(true);
        setJobId(row.original.jobDetails._id);
        setShovelerId(row.original.shovelerId);
    }

    const getTimeDifference = (postedTime, requestedTime) => {
        const diffMs = new Date(requestedTime) - new Date(postedTime);
        const diffSeconds = Math.floor(diffMs / 1000); // Convert to seconds

        if (diffSeconds < 60) return `${diffSeconds} seconds`;
        const diffMinutes = Math.floor(diffSeconds / 60);
        if (diffMinutes < 60) return `${diffMinutes} minutes`;
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} hours`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `${diffDays} days`;
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) return `${diffMonths} months`;

        return `${Math.floor(diffMonths / 12)} years`;
    };


    const columns = React.useMemo(
        () => [
            {
                Header: 'Job Status',
                accessor: 'jobstatus',
                Cell: ({ row }) => (
                    <div>
                        {row.original.jobDetails.jobStatus}
                    </div>
                ),
            },
            {
                Header: 'Homeowner name',
                accessor: 'homeowner.name',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.firstName} {row.original.userDetails.lastName}
                    </div>
                ),
            },
            {
                Header: 'Homeowner username',
                accessor: 'homeowner.userName',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.userName}
                    </div>
                ),
            },
            {
                Header: 'Shoveller name',
                accessor: 'shoveller.name',
                Cell: ({ row }) => {
                    const shovellers = row.original.jobDetails.ShovelerInfo;
                    if (!shovellers) return <div>Null</div>
                    const { firstName, lastName } = shovellers[0].ShovelerId;
                    return <div>
                        {firstName} {lastName}
                    </div>
                },
            },
            {
                Header: 'Shoveller username',
                accessor: 'shoveller.userName',
                Cell: ({ row }) => (
                    <div>
                        {row.original.jobDetails.ShovelerInfo.length ? row.original.jobDetails.ShovelerInfo[0].ShovelerId.userName : "N/A"}
                    </div>
                ),
            },
            {
                Header: 'Address', accessor: 'address',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.address}
                    </div>
                ),
            },
            {
                Header: 'Phone Number', accessor: 'phone',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.phone}
                    </div>
                ),
            },
            {
                Header: 'Email', accessor: 'email',
                Cell: ({ row }) => (
                    <div>
                        {row.original.userDetails.email}
                    </div>
                ),
            },
            {
                Header: 'Post Date', accessor: 'PostDate',
                Cell: ({ row }) => (
                    <div>
                        {new Date(row.original.jobDetails.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </div>
                ),
            },
            {
                Header: 'Authorization Date Time', accessor: 'authorizationDatetime',
                Cell: ({ row }) => {
                    const date = row.original.jobDetails.authorizationDate;
                    return (<div>
                        {date ? new Date(date).toLocaleString("en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        }) : "-"}
                    </div>)
                },
            },
            {
                Header: 'Instructions by homeowner', accessor: 'instructions',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    const serviceInstruction = serviceDetail.serviceInstruction ? serviceDetail.serviceInstruction.join(" ") : "";
                    if (!serviceInstruction) return <div>Null</div>;
                    return (
                        <div>
                            {serviceInstruction}
                        </div>
                    )
                },
            },
            {
                Header: 'Tool by homeowner', accessor: 'toolbyhomeowner',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    const serviceEquipment = serviceDetail.serviceEquipment ? serviceDetail.serviceEquipment.join(",") : "";
                    if (!serviceEquipment) return <div>Null</div>;
                    return (
                        <div>
                            {serviceEquipment}
                        </div>
                    )
                },
            },
            {
                Header: 'Request Date', accessor: 'RequestDate',
                Cell: ({ row }) => {
                    const shovelerInfo = row.original.jobDetails.ShovelerInfo;
                    if (!shovelerInfo) return <div>Null</div>
                    const requestedTime = shovelerInfo[0].requestedTime;
                    if (!requestedTime) return <div>Null</div>
                    return (
                        <div>
                            {new Date(requestedTime).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </div>
                    )
                },
            },
            {
                Header: 'House Owner Job Status',
                accessor: 'status',
                Cell: ({ row }) => {
                    const statusArray = ['Pending', 'Completed', 'In-progress', 'Canceled'];
                    const shovelers = row.original.jobDetails.ShovelerInfo;
                    if (!shovelers.length) return (<button>Null</button>);
                    const currentStatus = shovelers[0].houseOwnerAction;
                    console.log(currentStatus, "Status", row.original)
                    const currentIndex = statusArray.indexOf(currentStatus);
                    const nextStatus = statusArray[(currentIndex + 1) % statusArray.length];

                    return (
                        <button
                            className={`px-3 py-1 rounded cursor-pointer bg-zinc-900 text-white`}
                            onClick={() => {
                                const updatedCustomers = [...jobs];
                                updatedCustomers[row.index].status = nextStatus;
                                setJobs(updatedCustomers);
                            }}
                        >
                            {currentStatus}

                        </button>

                    );
                },
            },

            {
                Header: 'Shovel Job Status',
                accessor: 'shovelStatus',
                Cell: ({ row }) => {
                    const statusArray = ['Pending', 'Completed', 'In-progress', 'Canceled'];
                    const shovelers = row.original.jobDetails.ShovelerInfo;
                    if (!shovelers.length) return (<button>Null</button>);
                    const currentShovelStatus = shovelers[0].shovellerAction;
                    const currentShovelIndex = statusArray.indexOf(currentShovelStatus);
                    const nextShovelStatus = statusArray[(currentShovelIndex + 1) % statusArray.length];

                    return (
                        <button
                            className={`px-3 py-1 rounded cursor-pointer bg-zinc-900 text-white`}
                            onClick={() => {
                                const updatedCustomers = [...jobs];
                                updatedCustomers[row.index].shovelStatus = nextShovelStatus;
                                setJobs(updatedCustomers);
                            }}
                        >
                            {currentShovelStatus}
                        </button>
                    );
                },
            },

            {
                Header: 'Type of Service', accessor: 'service',
                Cell: ({ row }) => {
                    const serviceDetail = row.original.jobDetails.serviceDetail;
                    if (!serviceDetail) return <div>Null</div>;
                    return (
                        <div>
                            {serviceDetail.service}
                        </div>

                    )
                },
            },
            {
                Header: 'Price Offered', accessor: 'price',
                Cell: ({ row }) => (
                    <div>
                        {row.original.jobDetails.paymentInfo.amount / 100}
                    </div>
                ),
            },
            {
                Header: 'Payment Capture',
                accessor: 'payment',
                Cell: ({ row }) => (
                    <button onClick={() => handleManualCaptureClick(row)} className="bg-black text-white px-1 py-1 rounded-md">Capture</button>
                ),
            },
            {
                Header: 'Match after posting',
                accessor: 'timeformatchafterposting',
                Cell: ({ row }) => {
                    const shovelerInfo = row.original.jobDetails.ShovelerInfo;
                    if (!shovelerInfo) return <div>Null</div>
                    const requestedTime = shovelerInfo[0].requestedTime;
                    if (!requestedTime) return <div>Null</div>
                    const postedTime = row.original.jobDetails.createdAt
                    if (!postedTime) return <div>Null</div>
                    const time = getTimeDifference(postedTime, requestedTime);

                    return (<div>{time}</div>)
                },
            },
            {
                Header: 'Homeowner Service Completed Time',
                accessor: 'homeownerservicecompletedtime',
                Cell: ({ row }) => {
                    let houseowner = '';
                    const shovelerInfo = row.original.jobDetails.ShovelerInfo;
                    if (!shovelerInfo) return <div>Null</div>
                    const houseOwnerCompletedDate = shovelerInfo[0].houseOwnerCompletedDate;
                    if (!houseOwnerCompletedDate) return <div>Null</div>
                    houseowner = new Date(houseOwnerCompletedDate).toLocaleString();
                    return (<div>{houseowner}</div>
                    )
                },
            },
            {
                Header: 'Shoveller Service Completed Time',
                accessor: 'shovellerservicecompletedtime',
                Cell: ({ row }) => {
                    let shoveller = '';
                    const shovelerInfo = row.original.jobDetails.ShovelerInfo;
                    if (!shovelerInfo) return <div>Null</div>
                    const shovellerCompletedDate = shovelerInfo[0].shovellerCompletedDate;
                    if (!shovellerCompletedDate) return <div>Null</div>
                    shoveller = new Date(shovellerCompletedDate).toLocaleString();
                    return (<div>{shoveller}</div>
                    )
                },
            },
            {
                Header: 'Chat Log',
                accessor: 'chat',
                Cell: ({ row }) => {

                    const [chat, setChat] = useState(undefined);

                    const jobId = row.original.jobDetails._id;

                    const fetchChats = async () => {
                        const url = "/job/admin/getchats/" + jobId
                        const res = await api.get(url);
                        const { data } = res.data;
                        if (!data.length) {
                            alert("No chat history !");
                            return;
                        }
                        setChat(data);
                    }

                    return (
                        <div>
                            {chat ? <button onClick={() => setChat(undefined)} className='fixed flex justify-center items-center top-0 left-0 z-50 bg-black bg-opacity-25 border shadow-lg w-full h-full overflow-scroll'>
                                <div className='bg-white rounded-lg text-start w-96 h-60 p-4 overflow-scroll'>
                                    {chat.map(({ message, sender, timestamp }, i) => (
                                        <p><b>{sender}</b>: {message} <span className='text-xs'><br />{new Date(timestamp).toLocaleString()}</span></p>
                                    ))}
                                </div>
                            </button> : ""}
                            <button onClick={() => fetchChats()} className='underline font-bold'>Chat</button>
                        </div>

                    )
                },
            },
            {
                Header: 'Action',
                accessor: 'actions',
                Cell: ({ row }) => {
                    const handleCancel = async (type) => {
                        const confirmCancel = window.confirm(
                            type === "shoveller"
                                ? "Are you sure you want to cancel this job as the shoveller?"
                                : "Are you sure you want to permanently cancel this job (by admin)?"
                        );
                        if (!confirmCancel) return;

                        try {
                            const jobId = row.original.jobDetails._id;
                            const shovellerId = row.original.jobDetails.ShovelerInfo?.[0]?.ShovelerId?._id;

                            const res =
                                type === "shoveller"
                                    ? await cancelJob(jobId, shovellerId)
                                    : await cancelJobByAdmin(jobId, shovellerId);

                            if (res.err) {
                                alert(res.err);
                            } else if (res.message) {
                                alert(res.message);
                                window.location.reload();
                            }
                        } catch (err) {
                            console.error("Cancel job failed:", err);
                            alert("Something went wrong while canceling the job.");
                        }
                    };

                    return (
                        <div className="flex gap-2">
                            {/* Cancel as shoveller */}
                            <button
                                onClick={() => handleCancel("shoveller")}
                                disabled={row.original.jobDetails.jobStatus !== "in-progress"}
                                className="bg-black disabled:bg-gray-500 hover:bg-gray-900 px-2 text-sm py-1 rounded-lg whitespace-nowrap text-white"
                            >
                                Cancel Job as Shoveller
                            </button>

                            {/* Cancel permanently by admin */}
                            <button
                                onClick={() => handleCancel("admin")}
                                disabled={row.original.jobDetails.jobStatus !== "in-progress"}
                                className="bg-red-600 disabled:bg-gray-400 hover:bg-red-700 px-2 text-sm py-1 rounded-lg whitespace-nowrap text-white"
                            >
                                Cancel Job Permanently
                            </button>
                        </div>
                    );
                },
            }



        ],
        [jobs]
    );

    const data = React.useMemo(() => {
        return jobs
            .filter((job) =>
                job.userDetails.userName.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
                // Convert dates to JavaScript Date objects (if not already)
                const dateA = new Date(a.userDetails.dateJoined);
                const dateB = new Date(b.userDetails.dateJoined);

                // Handle invalid date parsing
                if (isNaN(dateA) || isNaN(dateB)) {
                    return 0; // If either date is invalid, keep the order unchanged
                }
                // Sort by "Newest First" or "Oldest First"
                if (dateSortBy === 'Newest First') {
                    return dateB - dateA; // Newest dates first
                } else if (dateSortBy === 'Oldest First') {
                    return dateA - dateB; // Oldest dates first
                } else {
                    return 0; // If dateSortBy is not valid, don't change order
                }
            });
    }, [jobs, searchTerm, dateSortBy]);


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: data,
            initialState: { pageIndex: 0, pageSize: 8 },
        },
        useSortBy,
        usePagination
    );

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };



    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!jobs.length) {
        return <p>No jobs available</p>;
    }

    const exportToCsv = async () => {
        const res = await allJobsInfo();
        const transformedData = res.map(job => {
            let obj = {};
            obj['Homeowner name'] = job.userDetails.firstName + " " + job.userDetails.lastName
            obj['Homeowner username'] = job.userDetails.userName
            const shoveller = job.jobDetails.ShovelerInfo[0].ShovelerId;
            obj['Shoveller name'] = shoveller.firstName + " " + shoveller.lastName
            obj['Shoveller username'] = shoveller.userName
            obj['Address'] = job.userDetails.address
            obj['Phone Number'] = job.userDetails.phone.toString()
            obj['Email'] = job.userDetails.email
            obj['Post Date'] = new Date(job.jobDetails.createdAt).toLocaleString()
            obj['Instructions by homeowner'] = job.jobDetails.serviceDetail.serviceInstruction?.join(" ");
            obj['Tool by homeowner'] = job.jobDetails.serviceDetail.serviceEquipment.join(" ");
            obj['Request Date'] = new Date(job.jobDetails.ShovelerInfo[0].requestedTime).toLocaleString()
            obj['Type of service'] = job.jobDetails.serviceDetail.service
            obj['Price Offered'] = job.jobDetails.paymentInfo.amount / 100
            obj['Match after posting'] = getTimeDifference(job.jobDetails.createdAt, job.jobDetails.ShovelerInfo[0].requestedTime)
            let shovellerDate = '';
            let houseownerDate = '';
            const shovelerInfo = job.jobDetails.ShovelerInfo;
            const houseOwnerCompletedDate = shovelerInfo[0].houseOwnerCompletedDate;
            const shovellerCompletedDate = shovelerInfo[0].shovellerCompletedDate;
            shovellerDate = new Date(shovellerCompletedDate).toLocaleString();
            houseownerDate = new Date(houseOwnerCompletedDate).toLocaleString();
            obj['Homeowner Service Completed Time'] = houseownerDate
            obj['Shoveller Service Completed Time'] = shovellerDate
            return obj;
        });
        const csv = Papa.unparse(transformedData);

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'jobs_data.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    return (
        <section className="mt-6 mb-12">
            <div className="">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col">
                        <h2 className="text-2xl font-semibold tracking-tight">All Jobs</h2>
                        <p className="text-sm text-muted-foreground">Active Jobs</p>
                    </div>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <div className="relative w-full sm:w-auto">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search..."
                                className="pl-10 w-full sm:w-[200px] lg:w-[250px]"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <Select value={dateSortBy} onValueChange={setDateSortBy}>
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="Sort by date" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Newest First">Newest First</SelectItem>
                                <SelectItem value="Oldest First">Oldest First</SelectItem>
                            </SelectContent>
                        </Select>

                        <Button variant="secondary" onClick={exportToCsv} className="w-full sm:w-auto">
                            Export to CSV
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <div className='overflow-x-auto'>
                    <table {...getTableProps()} className="w-full mt-10 text-sm font-medium tracking-normal text-gray-400 border-collapse">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())} className="px-4 py-2 text-left border-b-0">
                                            {column.render('Header')}
                                            {column.canSort && (
                                                <span className={`ml-2 ${column.isSorted ? '' : 'invisible'}`}>
                                                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                </span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} className="border-b-0">
                            {page.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="border-b-0">
                                        {row.cells.map(cell => {
                                            return <td {...cell.getCellProps()} className="px-4 py-2 border-b-0">{cell.render('Cell')}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <ReactPaginate
                    className='pagination-container flex justify-end me-32 mt-10 gap-x-5'
                    previousLabel={
                        <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                            &lt;
                        </span>
                    }
                    nextLabel={
                        <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                            &gt;
                        </span>
                    }
                    breakLabel={<span className="mx-2 text-gray-600">...</span>}
                    breakClassName={`break-me`}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    pageCount={Math.ceil(totalCount / rowsPerPage)}
                    forcePage={currentPage}
                    onPageChange={handlePageClick}
                    containerClassName={`flex items-center`}
                    pageLinkClassName={`bg-gray-200 px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out`}
                    activeLinkClassName={`bg-zinc-900 text-white rounded-md`}
                    disabledClassName={`opacity-50 cursor-not-allowed`}
                />

            </div>
            {/* Use the reusable confirmation modal */}
            <ConfirmationModal
                showModal={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCapture}
                title="Capture Payment"
                message="Are you sure you want to capture the paymnent for this job?"
            />
        </section>


    );
}

export default CustomerTable;
